import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';

import AppContext from '@/contexts/appContext';
import useAPI from '@/hooks/useAPI';
import EncountersRepository from '@/repositories/EncountersRepository';
import {
  ENCOUNTER_TYPES,
  Encounter,
  EncounterChecklistItem,
  EncounterLookupResponse,
  EncounterSummary,
  MarkNonBillablePayload,
  NonBillableReason
} from '@/types/encounter';
import RepositoryResponse from '@/types/repositoryResponse';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

const concatEncounters = (
  response: RepositoryResponse<EncounterLookupResponse>
): EncounterSummary[] => {
  if (response.data) {
    const { data: encounter } = response;
    const concatenatedEncounters = [encounter, ...encounter.related_encounters];
    const sortedByBillableFirst = concatenatedEncounters.sort((encounter) =>
      encounter.maybe_billable ? -1 : 1
    );
    return sortedByBillableFirst;
  }
  return [];
};

export const useEncountersByHealthieId = ({
  healthieId,
  encounterType,
  enabled = true
}: {
  healthieId?: string | null;
  encounterType?: string | null;
  enabled?: boolean;
}) => {
  const encountersRepository = EncountersRepository();

  return useAPI({
    queryKey: ['getEncountersByHealthieId', healthieId],
    queryFn: () => {
      if (!healthieId || !encounterType)
        return Promise.reject('Missing healthieId or encounterType');
      return encountersRepository.getEncounterByHealthieId({
        encounterType,
        healthieId
      });
    },
    enabled,
    select: concatEncounters
  });
};

export const useEncounterById = ({
  encounterId,
  encounterType,
  enabled = true
}: {
  encounterId?: string | null;
  encounterType?: string | null;
  enabled?: boolean;
}) => {
  const encountersRepository = EncountersRepository();

  return useAPI({
    queryKey: ['getEncounterById', encounterId],
    queryFn: () => {
      if (!encounterId || !encounterType)
        return Promise.reject('Missing encounterId or encounterType');
      return encountersRepository.getEncounterById({
        encounterType,
        encounterId
      });
    },
    enabled,
    select: (response: RepositoryResponse<Encounter>) => response.data
  });
};

export const useAttendanceChecklist = ({
  encounterId,
  enabled = true
}: {
  encounterId?: string | null;
  enabled?: boolean;
}) => {
  const encountersRepository = EncountersRepository();

  return useAPI({
    queryKey: ['getAttendanceChecklist', encounterId],
    queryFn: () => {
      if (!encounterId) return Promise.reject('Missing encounterId');
      return encountersRepository.getAttendanceChecklist({ encounterId });
    },
    enabled: enabled && !!encounterId,
    select: (response: RepositoryResponse<EncounterChecklistItem[]>) =>
      // Missing information is hidden from providers for now [AP-9095]
      (response.data ?? []).filter(({ key }) => key !== 'missing_information')
    // select: (response: RepositoryResponse<EncounterChecklistItem[]>) => response.data ?? []
  });
};

/**
 * Regenerates an attendance's billing item and stores the checklist that comes
 * back, so the checklist and billing cards both show the current state.
 */
export const useRefreshAttendanceChecklist = ({ encounterId }: { encounterId: string }) => {
  const { addMessage } = useContext(AppContext);
  const encountersRepository = EncountersRepository();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => encountersRepository.refreshAttendanceChecklist({ encounterId }),
    onSuccess: (response) => {
      if (response.isError) {
        addMessage({ level: 'error', text: 'Failed to refresh the checklist' });
        return;
      }
      queryClient.setQueryData(['getAttendanceChecklist', encounterId], response);
      // The billing card reads the billing item off the encounter
      queryClient.invalidateQueries({ queryKey: ['getEncounterById', encounterId] });
    },
    onError: (error: AxiosError) => addMessage(mapAxiosErrorMessages(error))
  });
};

export const useNonBillableReasons = (enabled = true) => {
  const encountersRepository = EncountersRepository();

  return useAPI({
    queryKey: ['getNonBillableReasons'],
    queryFn: () => encountersRepository.getNonBillableReasons(),
    enabled,
    select: (response: RepositoryResponse<NonBillableReason[]>) => {
      return response.data?.map((reason) => ({ value: reason.id, label: reason.name }));
    }
  });
};

export const useMarkAsNonBillable = ({
  encounterId,
  encounterType
}: {
  encounterId: string;
  encounterType: ENCOUNTER_TYPES;
}) => {
  const { addMessage } = useContext(AppContext);
  const encountersRepository = EncountersRepository();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (nonBillableReason: MarkNonBillablePayload) =>
      encountersRepository.markItemAsNonBillable({
        encounterType,
        encounterId,
        nonBillableReason
      }),
    onSuccess: ({ data }) => {
      data && queryClient.invalidateQueries({ queryKey: ['getEncounterById', data.encounter_id] });
      addMessage([{ level: 'success', text: 'Successfully marked as non billable' }]);
      navigate(-1);
    },
    onError: (error: AxiosError) => addMessage(mapAxiosErrorMessages(error))
  });
};

export const useMarkAsBillable = ({
  encounterType,
  encounterId
}: {
  encounterType: ENCOUNTER_TYPES;
  encounterId: string;
}) => {
  const { addMessage } = useContext(AppContext);
  const encountersRepository = EncountersRepository();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      encountersRepository.markItemAsBillable({
        encounterType,
        encounterId
      }),
    onSuccess: ({ data }) => {
      data && queryClient.invalidateQueries({ queryKey: ['getEncounterById', data.encounter_id] });
      addMessage([{ level: 'success', text: 'Successfully marked as billable' }]);
    },
    onError: (error: AxiosError) => addMessage(mapAxiosErrorMessages(error))
  });
};
